import { styled, css, withTheme } from '@vebox/etui-system';
import { PureComponent, createRef, createElement } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2, c as _defineProperty, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Label } from '@vebox/etui-system-elements';
import { FormItem } from '../FormItem/FormItem.js';
import MultiSwitcherPoint from './MultiSwitcherPoint.js';
import { POINT_POSITION } from './utils/index.js';

const MultiSwitcherDiv = styled(Div)`
  position: relative;
  display: flex;
  justify-content: space-evenly;
  text-align: left;
  width: 100%;

  > * {
    flex: 1 1 0;
  }

  &::after {
    content: '';
    position: absolute;
    left: ${({
  theme
}) => theme.spacing.xs}px;
    right: ${({
  theme
}) => theme.spacing.xs}px;
    bottom: 250%;
    border-top: 2px solid ${({
  theme
}) => theme.palette.grey[40]};
    pointer-events: none;
  }
`;

const _excluded = ["theme"];
const PointChecked = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    position: 'absolute',
    bottom: '250%',
    width: '42px',
    height: '42px',
    bg: theme.palette.primary.main,
    boxShadow: theme.shadows[3],
    zIndex: theme.zIndex.multiSwitcher
  }, props);
})`
  border-radius: 50%;
  font-size: 0;
  transform: translate(0, 50%);

  ${({
  disableAnimation
}) => !disableAnimation && css`
      transition: all 0.2s cubic-bezier(0.4, 0.25, 0.3, 1);
    `};

  ${({
  moveTo,
  offset
}) => {
  if (moveTo) {
    return css`
        transform: translate(calc((${offset}px + 0px) * ${moveTo}), 50%);
      `;
  }
  return ``;
}};

  ${({
  isRightEnd,
  isLeftEnd,
  moveTo,
  offset
}) => {
  if (offset) {
    if (isLeftEnd) {
      return css`
          left: 0;
        `;
    }
    if (isRightEnd) {
      return css`
          right: calc((${offset}px + 0px) * ${moveTo});
        `;
    }
    return css`
        left: calc(${offset}px / 2 - 42px / 2);
      `;
  }
  return ``;
}};

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    width: calc(42px - 60%);
    height: calc(42px - 60%);
    border-width: 4px;
    border-style: solid;
    border-color: ${({
  theme
}) => theme.palette.black[80]};
    background-color: ${({
  theme
}) => theme.palette.info.main};
    transform: translate(-50%, -50%);
    box-sizing: border-box;
  }
`;

class MultiSwitcher extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "pointRef", /*#__PURE__*/createRef());
    _defineProperty(this, "getSelectedPointName", index => {
      const {
        length
      } = this.props.points;
      switch (index) {
        case 0:
          return POINT_POSITION.LEFT;
        case length - 1:
          return POINT_POSITION.RIGHT;
        default:
          return POINT_POSITION.DEFAULT;
      }
    });
    _defineProperty(this, "createPoints", points => {
      return points.map((point, index) => {
        const title = point.title.toLowerCase();
        const sideOf = this.getSelectedPointName(index);
        const htmlRef = /*#__PURE__*/createRef();
        return /*#__PURE__*/createElement(MultiSwitcherPoint, {
          key: title,
          value: point.value,
          title: title,
          checked: this.state.value === point.value,
          onChange: this.handleChange,
          sideOf: sideOf,
          htmlRef: htmlRef
        });
      });
    });
    _defineProperty(this, "handleOnChangeProp", (value, shouldNotTriggerOnChange = false) => {
      const {
        onChange
      } = this.props;
      const selectedItem = this.props.points.find(point => point.value === value);
      if (onChange && !shouldNotTriggerOnChange && selectedItem) {
        onChange(selectedItem);
      }
    });
    this.state = {
      shouldAnimate: false,
      value: props.selectedValue,
      parentWidth: 0,
      startPos: null,
      endPos: null,
      isSwiping: false
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.getParentWidth = this.getParentWidth.bind(this);
  }
  componentDidMount() {
    /* Adding eventListener for a case
    if window size will be dynamically changed */
    window.addEventListener('resize', this.getParentWidth);
    this.getParentWidth();
    setTimeout(() => {
      this.setState({
        shouldAnimate: true
      });
    }, this.props.initialAnimationDelayMs);
  }
  componentDidUpdate(prevProps) {
    if (this.props.selectedValue !== prevProps.selectedValue) {
      this.handleCurrentPointChange(this.props.selectedValue, true);
    }
  }
  getParentWidth() {
    if (this.pointRef && this.pointRef.current) {
      this.setState({
        parentWidth: this.pointRef.current.clientWidth
      });
    }
  }
  getSelectedPointIndex() {
    return this.props.points.findIndex(point => point.value === this.state.value);
  }
  handleCurrentPointChange(value, shouldNotTriggerOnChange = false) {
    const selectedValue = parseInt(value, 10);
    this.setState({
      value: selectedValue
    }, () => this.handleOnChangeProp(selectedValue, shouldNotTriggerOnChange));
  }
  handleChange(event) {
    this.handleCurrentPointChange(event.target.value);
  }
  handleTouchStart(event) {
    event.stopPropagation();
    this.setState({
      startPos: event.targetTouches[0].clientX
    });
    if (this.props.onTouchStart) {
      this.props.onTouchStart();
    }
  }
  handleTouchMove(event) {
    event.stopPropagation();
    this.setState({
      isSwiping: true,
      endPos: event.targetTouches[0].clientX
    });
  }
  handleTouchEnd() {
    this.setState({
      isSwiping: false
    });
    this.handleSwipe();
    return null;
  }
  handleSwipe() {
    const {
      startPos,
      endPos,
      isSwiping
    } = this.state;
    if (isSwiping) {
      if (endPos > startPos) {
        this.swipeRight();
      } else if (endPos !== null && endPos < startPos) {
        this.swipeLeft();
      }
    }
  }
  canSwipeRight() {
    const selectedValueIndex = this.getSelectedPointIndex();
    return selectedValueIndex + 1 < this.props.points.length;
  }
  canSwipeLeft() {
    const selectedValueIndex = this.getSelectedPointIndex();
    return selectedValueIndex > 0;
  }
  swipeRight() {
    if (this.canSwipeRight()) {
      const selectedIndex = this.getSelectedPointIndex();
      const nextPoint = this.props.points[selectedIndex + 1];
      this.handleCurrentPointChange(nextPoint.value);
    }
  }
  swipeLeft() {
    if (this.canSwipeLeft()) {
      const selectedIndex = this.getSelectedPointIndex();
      const nextPoint = this.props.points[selectedIndex - 1];
      this.handleCurrentPointChange(nextPoint.value);
    }
  }
  renderPoints() {
    const {
      points,
      styles
    } = this.props;
    if (Array.isArray(points)) {
      const resPoints = this.createPoints(points);
      const moveToIndex = this.getSelectedPointIndex();
      const selectedPointName = this.getSelectedPointName(moveToIndex);
      if (moveToIndex !== -1) {
        resPoints.unshift(/*#__PURE__*/createElement(PointChecked, _extends({
          testId: "pointChecked",
          key: "unique",
          moveTo: moveToIndex,
          offset: this.state.parentWidth / points.length,
          onTouchStart: this.handleTouchStart,
          onTouchMove: this.handleTouchMove,
          onTouchEnd: this.handleTouchEnd,
          isLeftEnd: selectedPointName === POINT_POSITION.LEFT,
          isRightEnd: selectedPointName === POINT_POSITION.RIGHT,
          disableAnimation: !this.state.shouldAnimate
        }, styles.pointChecked)));
      }
      return resPoints;
    }
    return null;
  }
  render() {
    const {
      label,
      styles,
      theme
    } = this.props;
    return /*#__PURE__*/createElement(FormItem, styles.root, /*#__PURE__*/createElement(Label, _extends({}, theme.typography.label, {
      color: theme.palette.secondary.main,
      fontSize: theme.typography.fontSizeSmall
    }, styles.label), label), /*#__PURE__*/createElement(Div, {
      display: "block",
      pt: `calc(${theme.spacing.xxlg}px + ${theme.spacing.xxlg}px)`
    }, /*#__PURE__*/createElement(MultiSwitcherDiv, {
      elRef: this.pointRef
    }, this.renderPoints())));
  }
}
_defineProperty(MultiSwitcher, "defaultProps", {
  initialAnimationDelayMs: 1000,
  styles: {}
});
var MultiSwitcher$1 = withTheme(MultiSwitcher);

export default MultiSwitcher$1;
